import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Users,
  Shield,
  User,
  Building2,
  Edit2,
  ChevronDown,
  Search,
  Copy,
  CheckCircle2,
} from 'lucide-react';
import { updateBusiness4FlyoAgent, updateBusiness4FlyoClient } from '../../services/api';

const clientTypeOptions = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
];

const countryCodeOptions = [
  { code: '+1', country: 'United States / Canada' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+91', country: 'India' },
  { code: '+61', country: 'Australia' },
  { code: '+971', country: 'United Arab Emirates' },
  { code: '+65', country: 'Singapore' },
  { code: '+81', country: 'Japan' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+86', country: 'China' },
  { code: '+66', country: 'Thailand' },
  { code: '+60', country: 'Malaysia' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
  { code: '+92', country: 'Pakistan' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+974', country: 'Qatar' },
].sort((a, b) => a.country.localeCompare(b.country));

const normalize = (value) => (value ?? '').toString().trim();

const toInputDate = (value) => {
  if (!value) return '';
  const dateValue = value.toString().split('T')[0];

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  const slashDate = dateValue.split('/');
  if (slashDate.length === 3) {
    const [day, month, year] = slashDate;
    if (year && month && day) {
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  return '';
};

const formatDate = (value) => {
  const inputValue = toInputDate(value);
  if (!inputValue) return 'N/A';
  const [year, month, day] = inputValue.split('-');
  return `${day}/${month}/${year}`;
};

const CountryCodeSelector = ({ value, onChange }) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredOptions = useMemo(() => {
    const query = searchText.toLowerCase();
    return countryCodeOptions.filter((option) => {
      const label = `${option.code} ${option.country}`.toLowerCase();
      return label.includes(query);
    });
  }, [searchText]);

  const selectedOption = useMemo(
    () => countryCodeOptions.find((option) => option.code === value),
    [value]
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev);
          setSearchText('');
        }}
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
      >
        <span>
          {selectedOption ? `${selectedOption.code} (${selectedOption.country})` : 'Select country code'}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                className="w-full rounded-md border border-gray-200 py-2 pl-8 pr-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Search by code or country"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">No country codes found</p>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={`${option.code}-${option.country}`}
                  type="button"
                  onClick={() => {
                    onChange(option.code);
                    setIsOpen(false);
                    setSearchText('');
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    value === option.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span className="font-semibold">{option.code}</span>
                  <span>{option.country}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const copyValue = async (value) => {
  if (!value) return;
  await navigator.clipboard.writeText(value);
};

const getClientForm = (client) => ({
  name: client?.name || '',
  type: client?.type || 'individual',
  phone: client?.phone || '',
  countryCode: client?.countryCode || '',
  companyName: client?.companyName || '',
  lastTicketDate: toInputDate(client?.lastTicketDate || client?.lastBookingDate),
});

const getAgentForm = (agent) => ({
  name: agent?.name || '',
  phone: agent?.phone || agent?.mobile || '',
  countryCode: agent?.countryCode || '',
  callOut: agent?.callOut || '',
});

const pickChangedClientFields = (original, current) => {
  const patch = {};

  ['name', 'type', 'phone', 'countryCode', 'companyName', 'lastTicketDate'].forEach((key) => {
    const originalValue = normalize(original[key]);
    const currentValue = normalize(current[key]);
    if (originalValue !== currentValue) {
      patch[key] = currentValue;
    }
  });

  return patch;
};

const pickChangedAgentFields = (original, current) => {
  const patch = {};

  ['name', 'phone', 'countryCode', 'callOut'].forEach((key) => {
    const originalValue = normalize(original[key]);
    const currentValue = normalize(current[key]);
    if (originalValue !== currentValue) {
      patch[key] = currentValue;
    }
  });

  return patch;
};

/**
 * Combined Clients & Agent tab for Business4Flyo
 * @param {Object} props - Component props
 * @param {Array} props.clients - Client list
 * @param {Object} props.agent - Agent data
 * @param {number} props.agentId - Agent ID used for update API
 * @param {Function} props.onUpdate - Refresh callback
 * @returns {JSX.Element} B4FClientsAgentTab component
 */
const B4FClientsAgentTab = ({ clients, agent, agentId, onUpdate }) => {
  const [copiedField, setCopiedField] = useState('');
  const [editingClientId, setEditingClientId] = useState(null);
  const [editingClientOriginal, setEditingClientOriginal] = useState(null);
  const [editingClientForm, setEditingClientForm] = useState(null);
  const [editingAgent, setEditingAgent] = useState(false);
  const [editingAgentOriginal, setEditingAgentOriginal] = useState(null);
  const [editingAgentForm, setEditingAgentForm] = useState(null);
  const [savingTarget, setSavingTarget] = useState('');

  const resolvedAgentId = agentId || agent?.agentId || agent?.id;

  const handleCopy = async (value, key) => {
    try {
      await copyValue(value);
      setCopiedField(key);
      setTimeout(() => setCopiedField(''), 1200);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const startClientEdit = (client) => {
    const form = getClientForm(client);
    setEditingClientId(client.clientId);
    setEditingClientOriginal(form);
    setEditingClientForm(form);
  };

  const cancelClientEdit = () => {
    setEditingClientId(null);
    setEditingClientOriginal(null);
    setEditingClientForm(null);
  };

  const saveClientEdit = async (client) => {
    if (!resolvedAgentId || !client?.clientId || !editingClientForm || !editingClientOriginal) {
      alert('Missing required client update data');
      return;
    }

    const changedFields = pickChangedClientFields(editingClientOriginal, editingClientForm);
    if (Object.keys(changedFields).length === 0) {
      cancelClientEdit();
      return;
    }

    try {
      setSavingTarget(`client-${client.clientId}`);
      await updateBusiness4FlyoClient({
        agentId: Number(resolvedAgentId),
        clientId: Number(client.clientId),
        ...changedFields,
      });
      cancelClientEdit();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Failed to update client: ${error.message}`);
    } finally {
      setSavingTarget('');
    }
  };

  const startAgentEdit = () => {
    if (!agent) return;
    const form = getAgentForm(agent);
    setEditingAgent(true);
    setEditingAgentOriginal(form);
    setEditingAgentForm(form);
  };

  const cancelAgentEdit = () => {
    setEditingAgent(false);
    setEditingAgentOriginal(null);
    setEditingAgentForm(null);
  };

  const saveAgentEdit = async () => {
    const targetAgentId = agent?.agentId || agent?.id;
    if (!resolvedAgentId || !targetAgentId || !editingAgentForm || !editingAgentOriginal) {
      alert('Missing required agent update data');
      return;
    }

    const changedFields = pickChangedAgentFields(editingAgentOriginal, editingAgentForm);
    if (Object.keys(changedFields).length === 0) {
      cancelAgentEdit();
      return;
    }

    try {
      setSavingTarget('agent');
      await updateBusiness4FlyoAgent({
        agentId: Number(resolvedAgentId),
        targetAgentId: Number(targetAgentId),
        ...changedFields,
      });
      cancelAgentEdit();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Failed to update agent: ${error.message}`);
    } finally {
      setSavingTarget('');
    }
  };

  return (
    <div className="space-y-8">
      <section>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Clients</h2>
            <p className="text-sm text-gray-500">
              {clients?.length ? `${clients.length} client${clients.length > 1 ? 's' : ''} linked to this ticket` : 'No clients available'}
            </p>
          </div>
        </div>

        {!clients?.length && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            No client details found.
          </div>
        )}

        <div className="space-y-4">
          {clients?.map((client) => {
            const isEditing = editingClientId === client.clientId;
            const clientPatch = isEditing && editingClientOriginal && editingClientForm
              ? pickChangedClientFields(editingClientOriginal, editingClientForm)
              : {};
            const hasClientChanges = Object.keys(clientPatch).length > 0;

            return (
              <div key={client.clientId} className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-4">
                  <div>
                    <div className="flex items-center gap-2">
                      {client.type === 'individual' ? <User className="h-4 w-4 text-blue-600" /> : <Building2 className="h-4 w-4 text-purple-600" />}
                      <h3 className="text-lg font-semibold text-gray-900">{client.name || 'Unnamed Client'}</h3>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Client ID: {client.clientId || 'N/A'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => (isEditing ? cancelClientEdit() : startClientEdit(client))}
                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 hover:bg-gray-100"
                    title="Edit client"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="p-5">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Name</label>
                          <input
                            type="text"
                            value={editingClientForm.name}
                            onChange={(event) => setEditingClientForm((prev) => ({ ...prev, name: event.target.value }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Type</label>
                          <select
                            value={editingClientForm.type}
                            onChange={(event) => setEditingClientForm((prev) => ({ ...prev, type: event.target.value }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          >
                            {clientTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Phone</label>
                          <input
                            type="text"
                            value={editingClientForm.phone}
                            onChange={(event) => setEditingClientForm((prev) => ({ ...prev, phone: event.target.value }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Country Code</label>
                          <CountryCodeSelector
                            value={editingClientForm.countryCode}
                            onChange={(value) => setEditingClientForm((prev) => ({ ...prev, countryCode: value }))}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Company Name</label>
                          <input
                            type="text"
                            value={editingClientForm.companyName}
                            onChange={(event) => setEditingClientForm((prev) => ({ ...prev, companyName: event.target.value }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Last Ticket Date</label>
                          <input
                            type="date"
                            value={editingClientForm.lastTicketDate}
                            onChange={(event) => setEditingClientForm((prev) => ({ ...prev, lastTicketDate: event.target.value }))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
                        <button
                          type="button"
                          onClick={cancelClientEdit}
                          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                          disabled={savingTarget === `client-${client.clientId}`}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => saveClientEdit(client)}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                          disabled={!hasClientChanges || savingTarget === `client-${client.clientId}`}
                        >
                          {savingTarget === `client-${client.clientId}` ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Email</p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-semibold text-gray-800">{client.email || 'N/A'}</p>
                          {client.email && (
                            <button
                              type="button"
                              onClick={() => handleCopy(client.email, `client-${client.clientId}-email`)}
                              className={`rounded-md p-1 ${copiedField === `client-${client.clientId}-email` ? 'bg-green-100 text-green-700' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                              title="Copy email"
                            >
                              {copiedField === `client-${client.clientId}-email` ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Phone</p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-800">{`${client.countryCode || ''} ${client.phone || ''}`.trim() || 'N/A'}</p>
                          {(client.countryCode || client.phone) && (
                            <button
                              type="button"
                              onClick={() => handleCopy(`${client.countryCode || ''}${client.phone || ''}`.trim(), `client-${client.clientId}-phone`)}
                              className={`rounded-md p-1 ${copiedField === `client-${client.clientId}-phone` ? 'bg-green-100 text-green-700' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                              title="Copy phone"
                            >
                              {copiedField === `client-${client.clientId}-phone` ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Client Type</p>
                        <p className="mt-2 text-sm font-semibold capitalize text-gray-800">{client.type || 'N/A'}</p>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Company Name</p>
                        <p className="mt-2 text-sm font-semibold text-gray-800">{client.companyName || 'N/A'}</p>
                      </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last Ticket Date</p>
                        <p className="mt-2 text-sm font-semibold text-gray-800">{formatDate(client.lastTicketDate || client.lastBookingDate)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="border-t border-gray-200" />

      <section>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600 text-white">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Agent Information</h2>
            <p className="text-sm text-gray-500">Manage editable agent fields</p>
          </div>
        </div>

        {!agent && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            No agent details available.
          </div>
        )}

        {agent && (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{agent.name || 'Unnamed Agent'}</h3>
                </div>
                <p className="mt-1 text-xs text-gray-500">Agent ID: {agent.agentId || agent.id || 'N/A'}</p>
              </div>
              <button
                type="button"
                onClick={() => (editingAgent ? cancelAgentEdit() : startAgentEdit())}
                className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 hover:bg-gray-100"
                title="Edit agent"
              >
                <Edit2 className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5">
              {editingAgent && editingAgentForm ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Name</label>
                      <input
                        type="text"
                        value={editingAgentForm.name}
                        onChange={(event) => setEditingAgentForm((prev) => ({ ...prev, name: event.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Phone</label>
                      <input
                        type="text"
                        value={editingAgentForm.phone}
                        onChange={(event) => setEditingAgentForm((prev) => ({ ...prev, phone: event.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Country Code</label>
                      <CountryCodeSelector
                        value={editingAgentForm.countryCode}
                        onChange={(value) => setEditingAgentForm((prev) => ({ ...prev, countryCode: value }))}
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Call Out</label>
                      <textarea
                        rows={3}
                        value={editingAgentForm.callOut}
                        onChange={(event) => setEditingAgentForm((prev) => ({ ...prev, callOut: event.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        placeholder="Best travel agency in town"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
                    <button
                      type="button"
                      onClick={cancelAgentEdit}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      disabled={savingTarget === 'agent'}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveAgentEdit}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                      disabled={Object.keys(pickChangedAgentFields(editingAgentOriginal || {}, editingAgentForm || {})).length === 0 || savingTarget === 'agent'}
                    >
                      {savingTarget === 'agent' ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Email</p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-gray-800">{agent.email || 'N/A'}</p>
                      {agent.email && (
                        <button
                          type="button"
                          onClick={() => handleCopy(agent.email, 'agent-email')}
                          className={`rounded-md p-1 ${copiedField === 'agent-email' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                          title="Copy email"
                        >
                          {copiedField === 'agent-email' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Phone</p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-800">{`${agent.countryCode || ''} ${agent.phone || agent.mobile || ''}`.trim() || 'N/A'}</p>
                      {(agent.countryCode || agent.phone || agent.mobile) && (
                        <button
                          type="button"
                          onClick={() => handleCopy(`${agent.countryCode || ''}${agent.phone || agent.mobile || ''}`.trim(), 'agent-phone')}
                          className={`rounded-md p-1 ${copiedField === 'agent-phone' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                          title="Copy phone"
                        >
                          {copiedField === 'agent-phone' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Call Out</p>
                    <p className="mt-2 text-sm font-semibold text-gray-800">{agent.callOut || 'N/A'}</p>
                  </div>

                  {agent.joinDate && (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Member Since</p>
                      <p className="mt-2 text-sm font-semibold text-gray-800">{formatDate(agent.joinDate)}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default B4FClientsAgentTab;
